// Practice launcher: one card per persona of my live/draft campaigns, the live-call panel, and the scorecard
// as soon as the call is scored.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { AiStatus, PersonaRow, PracticeFinishResponse, PracticeSessionRow } from '../../../src/shared/api-ai';
import { Empty, ErrorBanner } from '../components/ui';
import { KIND_LABEL } from './labels';
import { PracticeCall, type PracticePhase } from './practiceCall';
import { ScorecardView } from './ScorecardView';

const PHASE_TEXT: Record<PracticePhase, string> = {
  idle: '',
  mic: 'Checking your microphone…',
  starting: 'Setting up the call…',
  connecting: 'Connecting… the prospect will pick up and speak first.',
  live: 'Live. You are the caller.',
  scoring: 'Call ended. Scoring your call (up to a minute)…',
  done: 'Scored.',
  error: '',
};

function mmss(sec: number): string {
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
}

export function Practice(props: { personas: PersonaRow[]; status: AiStatus | null; onScored: (s: PracticeSessionRow) => void }) {
  const [active, setActive] = useState<PersonaRow | null>(null);
  const [phase, setPhase] = useState<PracticePhase>('idle');
  const [talking, setTalking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PracticeFinishResponse | null>(null);
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [maxMinutes, setMaxMinutes] = useState(6);
  const call = useRef<PracticeCall | null>(null);

  const busy = phase === 'mic' || phase === 'starting' || phase === 'connecting' || phase === 'live' || phase === 'scoring';

  useEffect(() => {
    if (phase !== 'live') return;
    const t0 = Date.now() - elapsed * 1000;
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 500);
    return () => clearInterval(id);
  }, [phase]);

  // Leaving the page mid-call hangs up (the session can still be scored from History).
  useEffect(() => () => call.current?.hangUp(), []);

  const start = async (p: PersonaRow) => {
    setActive(p); setError(null); setResult(null); setTalking(false); setMuted(false); setElapsed(0);
    const pc = new PracticeCall({
      phase: setPhase,
      agentTalking: setTalking,
      error: setError,
      scored: (r) => { setResult(r); props.onScored(r.session); },
    });
    call.current = pc;
    const s = await pc.start(p.id);
    if (s) setMaxMinutes(s.maxMinutes);
  };

  if (!props.status?.practice) {
    return <Empty>{props.status?.message ?? 'Practice calls are not set up on this server yet.'}</Empty>;
  }
  if (props.personas.length === 0) {
    return <Empty>No practice partners yet. They appear once you're assigned to a campaign and an admin has synced its personas.</Empty>;
  }

  const campaigns = [...new Map(props.personas.map((p) => [p.campaignId, p])).values()];

  return (
    <div class="stack" style="gap:14px">
      <div class="tr-notice">
        Practice calls are with an AI role-play prospect. They are <b>recorded and transcribed by Retell</b> (our voice-AI provider) and
        <b> scored by AI</b> against the call rubric. Use your headset, and never say real customer details.
        Three passes across at least two prospect types certify you once per niche (every campaign in it), and it doesn't expire.
      </div>

      {active && phase !== 'idle' ? (
        <div class="card tr-live stack" aria-live="polite">
          <div class="row" style="justify-content:space-between">
            <div class="stack" style="gap:2px">
              <span class="eyebrow">{active.campaignName} · {KIND_LABEL[active.kind]}</span>
              <h2 style="margin:0">{active.name}</h2>
            </div>
            {phase === 'live' ? <span class="mono" style="font-size:20px" title={`The call ends by itself after about ${maxMinutes} minutes`}>{mmss(elapsed)}</span> : null}
          </div>
          {PHASE_TEXT[phase] ? (
            <div class="row" style="gap:8px">
              {phase === 'live' ? <span class={`tr-dot ${talking ? 'on' : ''}`} title={talking ? 'Prospect is talking' : 'Your turn'} /> : null}
              <span>{PHASE_TEXT[phase]}{phase === 'live' ? (talking ? ' Prospect is talking.' : '') : ''}</span>
            </div>
          ) : null}
          <ErrorBanner error={error} />
          {phase === 'live' || phase === 'connecting' ? (
            <div class="row">
              <button class="btn" onClick={() => setMuted(call.current?.toggleMute() ?? false)} disabled={phase !== 'live'}>{muted ? 'Unmute' : 'Mute'}</button>
              <button class="btn btn-danger" onClick={() => call.current?.hangUp()}>Hang up</button>
            </div>
          ) : null}
          {result ? (
            result.session.scorecard
              ? <ScorecardView card={result.session.scorecard} />
              : <div class="chip chip-warn" style="display:block;padding:8px 10px">{result.message ?? result.session.note}</div>
          ) : null}
          {phase === 'done' || phase === 'error' ? (
            <div class="row">
              <button class="btn btn-primary" onClick={() => start(active)}>Call {active.name} again</button>
              <button class="btn" onClick={() => { setActive(null); setPhase('idle'); setResult(null); setError(null); }}>Close</button>
            </div>
          ) : null}
        </div>
      ) : null}

      {campaigns.map((cp) => (
        <div class="stack" style="gap:8px">
          <div class="row" style="gap:8px">
            <h3 style="margin:0">{cp.campaignName}</h3>
            <span class={`chip ${cp.campaignStatus === 'live' ? 'chip-ok' : ''}`}>{cp.campaignStatus}</span>
          </div>
          <div class="tr-grid">
            {props.personas.filter((p) => p.campaignId === cp.campaignId).map((p) => (
              <div class="card stack" style="gap:6px">
                <div class="row" style="justify-content:space-between">
                  <span class="chip">{KIND_LABEL[p.kind]}</span>
                  {p.ready ? null : <span class="chip chip-warn" title="An admin needs to sync this campaign's personas to Retell">Not set up</span>}
                </div>
                <div style="font-weight:700">{p.name}</div>
                <div class="muted" style="font-size:13px">{p.brief}</div>
                <div>
                  <button class="btn btn-primary btn-sm" disabled={!p.ready || busy} onClick={() => start(p)}
                    title={p.ready ? 'Starts a practice call in this browser tab' : 'Not set up on Retell yet'}>
                    Practice call
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
